import { RightOutlined, LinkOutlined } from '@ant-design/icons';
import { Divider, Flex, Typography, List as AntList } from 'antd';
import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { t, jt } from 'ttag';

import Loading from 'common/components/Loading';
import { EVENTMOBI_URLS, SUPPORT_URLS } from 'common/constants/ExternalUrls';
import { unsetOrganization } from 'common/state/organization';
import { getFirstName, getLastName, getEmail, getIsStaff } from 'common/state/user';

import { list as listFn } from './service';

const { Title, Text } = Typography;

const MANAGEMOBI_ROUTE = '/managemobi';

/*
Note: EXP is available in more languages than just English and German,
but only those two are defined here because the support site is currently
only in EN and DE.
Portuguese also exists in Support but it's completely empty.
Missing languages fallback to EN.
*/

const LIST_WIDTH = 450;

const clickableItemStyle = { cursor: 'pointer' };

const List = styled(AntList)`
  .ant-list-item-meta-title {
    font-weight: 400;
  }
`;

function OrgList() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const isStaff = useSelector(getIsStaff);
  const firstName = useSelector(getFirstName);
  const lastName = useSelector(getLastName);
  const email = useSelector(getEmail);
  const displayName = firstName || lastName ? [firstName, lastName].join(' ') : email;

  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function effect() {
      try {
        const result = await listFn();
        setList(result);
      } finally {
        dispatch(unsetOrganization());
        setLoading(false);
      }
    }

    effect();
  }, [dispatch]);

  if (loading) {
    return <Loading />;
  }

  const EMLink = (
    <a
      key="em"
      href={EVENTMOBI_URLS.HOME_WITH_UTM}
      rel="nofollow noopener noreferrer"
      target="_blank"
    >
      EventMobi
    </a>
  );

  return (
    <Flex vertical align="center" gap="middle">
      <Title level={2} style={{ marginTop: 52 }}>{t`Hello ${displayName}`}</Title>
      <Title
        level={5}
        style={{ marginTop: 0 }}
      >{t`Welcome back to the EventMobi Experience Manager`}</Title>
      <Divider style={{ marginBlock: 8 }} />
      <Title
        level={5}
        style={{ marginTop: 0, fontWeight: 400 }}
      >{t`To get started, please select an organization:`}</Title>

      <List
        bordered
        style={{ width: LIST_WIDTH }}
        dataSource={list}
        renderItem={item => (
          <List.Item style={clickableItemStyle} onClick={() => navigate(`${item?.id}`)}>
            <List.Item.Meta title={item.name} />
            <RightOutlined />
          </List.Item>
        )}
      />

      {isStaff && (
        <List
          bordered
          style={{ width: LIST_WIDTH }}
          dataSource={[{ id: 'managemobi' }]}
          renderItem={() => (
            <List.Item style={clickableItemStyle} onClick={() => navigate(MANAGEMOBI_ROUTE)}>
              <List.Item.Meta title={<Text type="danger">{t`ManageMobi`}</Text>} />
              <RightOutlined />
            </List.Item>
          )}
        />
      )}

      <Divider style={{ width: LIST_WIDTH, minWidth: LIST_WIDTH }} />

      <List
        bordered
        style={{ width: LIST_WIDTH }}
        dataSource={[
          { key: 'whats-new', title: t`New Features & Updates`, href: SUPPORT_URLS.WHATS_NEW },
          {
            key: 'knowledge-base',
            title: t`EventMobi Knowledge Base`,
            href: SUPPORT_URLS.KNOWLEDGE_BASE,
          },
        ]}
        renderItem={item => (
          <List.Item
            key={item.key}
            style={clickableItemStyle}
            onClick={() => window.open(item.href, '_blank', 'noopener,noreferrer')}
          >
            <List.Item.Meta title={item.title} />
            <LinkOutlined />
          </List.Item>
        )}
      />

      <Flex
        justify="space-between"
        align="center"
        style={{ width: LIST_WIDTH, marginTop: 16, fontSize: 12 }}
      >
        <Flex align="center">
          <a href={EVENTMOBI_URLS.TERMS_OF_USE} rel="nofollow noopener noreferrer" target="_blank">
            {t`Terms of Use`}
          </a>
          <Divider orientation="vertical" />
          <a
            href={EVENTMOBI_URLS.PRIVACY_POLICY}
            rel="nofollow noopener noreferrer"
            target="_blank"
          >
            {t`Privacy Policy`}
          </a>
        </Flex>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {jt`Powered by ${EMLink}™`}
        </Text>
      </Flex>
    </Flex>
  );
}

export default OrgList;
